import { Component, Input, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectDateAndTimeService } from '../../modules/mortgages/select-date-and-time/select-date-and-time.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Params } from '@angular/router';
import * as moment from 'moment';
import 'moment/locale/en-gb';

moment.locale('en-gb');
type DateInput = Date | string | moment.Moment | null | undefined;

@Component({
  selector: 'hf-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DatePickerComponent,
      multi: true
    }
  ]
})
export class DatePickerComponent implements OnDestroy, ControlValueAccessor {
  @Input() public label: string;
  @Input() public routerLink: any[] = ['./selectDateAndTime'];
  @Input() public propertyName: string;
  @Input() public onlyDate: boolean;
  @Input() public minDate: Date | 'none' | string | null = null;
  @Input() public maxDate: Date | string | null = null;
  @Input() public isInvalid: boolean;

  public value: Date | null = null;
  public valueLocalDate: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(private selectDateAndTimeService: SelectDateAndTimeService) {
    this.selectDateAndTimeService.dateSelected$.pipe(takeUntil(this.destroy$)).subscribe((dateProperty) => {
      if (dateProperty.name !== this.propertyName) return;

      const picked = this.asDate(dateProperty.date);
      this.value = picked;
      this.valueLocalDate = picked ? moment(picked).format('YYYY-MM-DD') : null;

      this.onChange(this.value);
    });
  }

  get displayDate(): Date | null {
    if (!this.valueLocalDate) return null;
    const m = moment(this.valueLocalDate, 'YYYY-MM-DD', true);
    return m.isValid() ? m.toDate() : null;
  }

  writeValue(obj: DateInput): void {
    const d = this.asDate(obj);
    this.value = d;
    this.valueLocalDate = d ? moment(d).format('YYYY-MM-DD') : null;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState?(isDisabled: boolean): void {}

  buildQueryParams(): Params {
    const params: Params = {
      name: this.propertyName,
      onlyDate: this.onlyDate,
      initialValue: this.valueLocalDate
    };

    const min = this.minDate === 'none' ? 'none' : this.toISO(this.minDate);
    const max = this.toISO(this.maxDate);

    if (min) params.minDate = min;
    if (max) params.maxDate = max;

    return params;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private onChange: (value: Date | null) => void = () => {};

  private asDate(value: DateInput): Date | null {
    if (!value) return null;

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    if (moment.isMoment(value)) {
      return value.isValid() ? value.toDate() : null;
    }

    if (typeof value === 'string') {
      // support localdate and ISO strings
      const m = moment(value, 'YYYY-MM-DD', true).isValid()
        ? moment(value, 'YYYY-MM-DD', true)
        : moment(value, moment.ISO_8601, true);

      return m.isValid() ? m.toDate() : null;
    }

    return null;
  }

  private toISO(value: any): string | null {
    const d = this.asDate(value);
    return d ? d.toISOString() : null;
  }
}
